"use client";

import {JSX} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {useTranslation} from "@/shared/i18n";
import {ExpandableTextWithButton} from "@/shared/ui/data-display/expandable-text";
import {Rating} from "@/shared/ui/data-display/rating";
import {Typography} from "@/shared/ui/data-display/typography";
import {assetPath} from "@/shared/utils/asset-path";

import "./styles/guest-reviews.scss";

interface GuestReviewsProps {
    rating?: number;
    reviewsCount?: number;
    reviewText: string;
    className?: string;
    showRating?: boolean;
}

export function GuestReviews({rating, reviewsCount, reviewText, className = "", showRating = true}: GuestReviewsProps): JSX.Element | null {
    const {t} = useTranslation("reviews");

    const handleScrollToReviews = (): void => {
        const reviewsSection = document.getElementById("reviews-section");
        if (reviewsSection) {
            reviewsSection.scrollIntoView({behavior: "smooth"});
        }
    };

    if (!(rating !== undefined && !!reviewsCount) || !reviewText) {
        return null;
    }

    return (
        <section className={`guest-reviews ${className}`}>
            <div className={`guest-reviews__wrapper ${!showRating ? "guest-reviews__wrapper--full" : ""}`}>
                {showRating && !!reviewsCount && (
                    <div className="guest-reviews__left">
                        <Rating value={rating} size="large" decimals={2} />

                        <button className="guest-reviews__reviews-link" onClick={handleScrollToReviews}>
                            <Typography as="span" size={14} weight={400} color="primary" className="guest-reviews__reviews-count">
                                {t("count", {count: reviewsCount})}
                            </Typography>
                        </button>
                    </div>
                )}

                <div className={`guest-reviews__right ${!showRating ? "guest-reviews__right--full" : ""}`}>
                    <div className="guest-reviews__header">
                        <Typography as="h2" size={{desktop: 20, mobile: 18}} weight={600} className="guest-reviews__title">
                            {t("whatGuestsSaying")}
                        </Typography>

                        <div className="guest-reviews__ai-badge">
                            <SmartImage
                                src={assetPath("/images/icons/diamond-ai.svg")}
                                alt=""
                                width={16}
                                height={16}
                                className="guest-reviews__ai-icon"
                            />
                            <Typography as="span" size={12} weight={400} color="paragraph2" className="guest-reviews__ai-text">
                                {t("aiGeneratedFromReviews")}
                            </Typography>
                        </div>
                    </div>

                    <ExpandableTextWithButton
                        maxLines={{desktop: 4, mobile: 4, tablet: 4}}
                        charCount={reviewText.length}
                        ssrEstimation={{
                            desktop: {width: 661, fontSize: 14},
                            tablet: {width: 661, fontSize: 14},
                            mobile: {width: 350, fontSize: 14},
                        }}
                        buttonSize="sm"
                        buttonClassName="guest-reviews__read-more-button"
                        className="guest-reviews__expandable"
                    >
                        <Typography as="p" size={14} weight={400} className="guest-reviews__text" innerHTML={reviewText} />
                    </ExpandableTextWithButton>
                </div>
            </div>
        </section>
    );
}
